import { type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { canDo } from '../booking-can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import {
  FIELD_ROW,
  FieldPair,
  PAIRED_FIELD,
  ProblemNotice,
  StaffInput,
  StaffSelect,
} from '../components/ui'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { refreshAfterChange } from '../refresh'
import { initialMoveForm, type MoveCurrent, type MoveForm, moveBody } from './move-booking'
import {
  commitChange,
  proposeChange,
  type ReschedulePatch,
  type RescheduleReason,
} from './timeline-reschedule'
import { patchSchedule } from './useReschedule'

type Detail = ApiSchemas['StaffBookingDetail']

/**
 * Only a booking still ahead of its pick-up moves, as on the Planning
 * (timeline-drag.ts); not a website draft waiting for its online share.
 */
const MOVABLE: readonly Detail['booking']['status'][] = ['confirmed']

/**
 * OPS-1: "Sposta…" on the booking page, the move that is not a drag: for a
 * finger, a keyboard, or a move past the Planning's window. One dialog with
 * the vehicle (an active one of the booking's category, or the one it has)
 * and the pick-up and return, Rome wall-clock as every date field. The
 * button is the confirmation (D11: a move keeps the record): on it, the
 * API is asked first (a dry run, so a refusal is explained with nothing
 * changed), then the move is saved under its own key, and the page, the
 * boards and the lists are refreshed from the server.
 */
export function MoveBookingAction({ detail, onMoved }: { detail: Detail; onMoved: () => void }) {
  const { messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const { booking } = detail
  const current: MoveCurrent = {
    vehicleId: booking.vehicle?.id ?? null,
    startsAt: booking.startsAt,
    endsAt: booking.endsAt,
  }
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<MoveForm>(() => initialMoveForm(current))
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<RescheduleReason | null>(null)
  const vehicles = useQuery({
    queryKey: ['vehicles', 'category', booking.category.id],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/vehicles', {
            params: { query: { categoryId: booking.category.id, status: 'active' } },
          }),
        ),
      ),
    enabled: open,
  })

  if (!canDo(detail.can)('reschedule') || !MOVABLE.includes(booking.status)) return null

  const built = moveBody(current, form)
  const ready = 'body' in built
  const reasons = 'problem' in built ? [m.booking.move[built.problem]] : []

  // A session that has ended is taken over by the lock, as every other dialog's request is.
  const patch: ReschedulePatch = async (request) => {
    const result = await patchSchedule(request)
    if (!result.ok) takeOver(result.problem)
    return result
  }

  function show() {
    setForm(initialMoveForm(current))
    setMessage(null)
    setOpen(true)
    dialog.current?.showModal()
  }

  async function submit() {
    if (!('body' in built)) return
    const change = { code: booking.code, body: built.body }
    setBusy(true)
    setMessage(null)
    try {
      const proposed = await proposeChange(patch, change, newIdempotencyKey)
      if (proposed.kind === 'refused') {
        setMessage(proposed.reason)
        return
      }
      const done = await commitChange(patch, null, change, null, newIdempotencyKey)
      if (done.kind === 'failed') {
        setMessage(done.reason)
        return
      }
      dialog.current?.close()
      onMoved()
      // The booking, Today, the Planning and the lists show the move at once.
      await refreshAfterChange(queryClient, booking.code)
    } catch {
      setMessage('moveFailed')
    } finally {
      setBusy(false)
    }
  }

  const set = (field: keyof MoveForm) => (value: string) =>
    setForm((before) => ({ ...before, [field]: value }))
  const keep = booking.vehicle
    ? format(m.booking.move.keep, { plate: booking.vehicle.plate })
    : m.booking.move.keepNone

  return (
    <>
      <Button variant="outline" className="rounded-sm" onClick={show}>
        {m.booking.move.action}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        title={format(m.booking.move.title, { code: booking.code })}
        confirmLabel={m.timeline.confirmMove}
        cancelLabel={m.timeline.cancelMove}
        busy={busy}
        ready={ready}
        reasons={reasons}
        onConfirm={() => void submit()}
        onClose={() => {
          setOpen(false)
          setMessage(null)
        }}
      >
        <p className="text-sm text-text-muted">{m.booking.move.hint}</p>
        <Field label={m.booking.vehicle}>
          {(control) => (
            <StaffSelect
              {...control}
              value={form.vehicleId}
              onChange={(event) => set('vehicleId')(event.target.value)}
            >
              <option value="">{keep}</option>
              {vehicles.data?.items
                .filter((vehicle) => vehicle.id !== current.vehicleId)
                .map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.plate} · {vehicle.make} {vehicle.model}
                  </option>
                ))}
            </StaffSelect>
          )}
        </Field>
        {/* The fleet did not load: the vehicle stays as it is, the dates can still move. */}
        <ProblemNotice problem={problemOf(vehicles.error)} />
        <div className={FIELD_ROW}>
          <FieldPair>
            <Field className={PAIRED_FIELD} label={m.newBooking.pickupDate} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={form.pickupDate}
                  onChange={(event) => set('pickupDate')(event.target.value)}
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={m.newBooking.pickupTime} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="time"
                  step={300}
                  value={form.pickupTime}
                  onChange={(event) => set('pickupTime')(event.target.value)}
                />
              )}
            </Field>
          </FieldPair>
          <FieldPair>
            <Field className={PAIRED_FIELD} label={m.newBooking.returnDate} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="date"
                  value={form.returnDate}
                  onChange={(event) => set('returnDate')(event.target.value)}
                />
              )}
            </Field>
            <Field className={PAIRED_FIELD} label={m.newBooking.returnTime} required>
              {(control) => (
                <StaffInput
                  {...control}
                  type="time"
                  step={300}
                  value={form.returnTime}
                  onChange={(event) => set('returnTime')(event.target.value)}
                />
              )}
            </Field>
          </FieldPair>
        </div>
        {message ? (
          // Refused or failed: said here, where the fields are, and nothing was changed.
          <Notice tone="danger" live="assertive">
            <p>{m.timeline[message]}</p>
          </Notice>
        ) : null}
      </ConfirmDialog>
    </>
  )
}
